import React, { useState, useRef, useCallback } from "react";
import { useSettingsAccountDetailsContext } from "../SettingsAccountDetailsApp";
import "../styles/settingsAccountDetailsStyles.css";
import SettingsPageWrapper from "../../../shared/SettingsPageWrapper";
import { formatPhone, formatEIN, maskEIN, einLabelForCountry, einPlaceholderForCountry, einMaxLengthForCountry } from "../../../shared/formattingUtils";
import { formatPostalCode } from "../../../shared/postalCodeUtils";
import {
  COUNTRY_OPTIONS,
  getRegionsForCountry,
  getRegionLabelForCountry,
} from "../../CustomFieldValues/constants/customFieldValuesConstants";

const SettingsAccountDetails = () => {
  const { team, updateTeam, businessTypes, settingsPath, lastEditedAt, changelogVersions } = useSettingsAccountDetailsContext();

  const [formState, setFormState] = useState({
    legal_business_name: team.legal_business_name || "",
    business_type: team.business_type || "",
    ein: team.ein ? formatEIN(team.ein, team.business_country || "US") : "",
    website_url: team.website_url || "",
    business_street_address: team.business_street_address || "",
    business_city: team.business_city || "",
    business_region: team.business_region || "",
    business_postal_code: team.business_postal_code ? formatPostalCode(team.business_postal_code) : "",
    business_country: team.business_country || "US",
    billing_contact_first_name: team.billing_contact_first_name || "",
    billing_contact_last_name: team.billing_contact_last_name || "",
    billing_contact_email: team.billing_contact_email || "",
    billing_contact_phone: team.billing_contact_phone ? formatPhone(team.billing_contact_phone) : "",
    billing_contact_title: team.billing_contact_title || "",
  });

  const [einFocused, setEinFocused] = useState(false);
  const [einEdited, setEinEdited] = useState(false);
  const debounceRef = useRef(null);

  const saveAccountDetails = useCallback((formData) => {
    updateTeam(formData);
  }, [updateTeam]);

  const debouncedSave = useCallback((formData) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => saveAccountDetails(formData), 1500);
  }, [saveAccountDetails]);

  const immediateSave = useCallback((formData) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    saveAccountDetails(formData);
  }, [saveAccountDetails]);

  const handleChange = (field, save = debouncedSave) => (e) => {
    const updated = { ...formState, [field]: e.target.value };
    setFormState(updated);
    save(updated);
  };

  const handleEINChange = (e) => {
    setEinEdited(true);
    const updated = { ...formState, ein: formatEIN(e.target.value, formState.business_country) };
    setFormState(updated);
    debouncedSave(updated);
  };

  const getEINDisplayValue = () => {
    if (einFocused || einEdited) {
      return formState.ein;
    }
    if (team.ein) {
      return maskEIN(team.ein, formState.business_country);
    }
    return formState.ein;
  };

  const handlePhoneChange = (e) => {
    const updated = { ...formState, billing_contact_phone: formatPhone(e.target.value) };
    setFormState(updated);
    debouncedSave(updated);
  };

  const handlePostalCodeChange = (e) => {
    const updated = { ...formState, business_postal_code: formatPostalCode(e.target.value, formState.business_country) };
    setFormState(updated);
    debouncedSave(updated);
  };

  const handleCountryChange = (e) => {
    const newCountry = e.target.value;
    const updated = {
      ...formState,
      business_country: newCountry,
      business_region: "",
      business_postal_code: "",
      ein: "",
    };
    setFormState(updated);
    setEinEdited(true);
    immediateSave(updated);
  };

  return (
    <SettingsPageWrapper settingsPath={settingsPath} title="Account" lastEditedAt={lastEditedAt} changelogVersions={changelogVersions}>
      <div className="team-member-card">
        <div className="team-member-card-header">
          <div className="team-member-card-header-left">
            <h2 className="team-member-card-title">Account</h2>
            <p className="team-member-card-subtitle">Manage your company information.</p>
          </div>
        </div>
        <hr className="account-divider" />

        <div className="d-flex flex-column flex-lg-row account-layout">
        <div className="account-form-col">
          <div>
            <div className="account-form-stack">
              <div className="account-section-label">Business Information</div>
              <div className="d-flex flex-column flex-md-row account-field-row">
                <div className="account-field">
                  <label className="account-field-label">Company Name</label>
                  <input
                    type="text"
                    className="form-control account-input"
                    value={formState.legal_business_name}
                    onChange={handleChange("legal_business_name")}
                    placeholder="Enter company name"
                  />
                </div>
                <div className="account-field">
                  <label className="account-field-label">Organization Type</label>
                  <select
                    className="form-control account-input"
                    value={formState.business_type}
                    onChange={handleChange("business_type", immediateSave)}
                  >
                    <option value="">Select a type...</option>
                    {businessTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="d-flex flex-column flex-md-row account-field-row">
                <div className="account-field">
                  <label className="account-field-label">Company Website URL</label>
                  <input
                    type="url"
                    className="form-control account-input"
                    value={formState.website_url}
                    onChange={handleChange("website_url")}
                    placeholder="https://"
                  />
                </div>
                <div className="account-field">
                  <label className="account-field-label">{einLabelForCountry(formState.business_country)}</label>
                  <input
                    type="text"
                    className="form-control account-input"
                    value={getEINDisplayValue()}
                    onChange={handleEINChange}
                    onFocus={() => setEinFocused(true)}
                    onBlur={() => setEinFocused(false)}
                    placeholder={einPlaceholderForCountry(formState.business_country)}
                    maxLength={einMaxLengthForCountry(formState.business_country)}
                  />
                </div>
              </div>

              <div className="account-section-label">Business Address</div>
              <div>
                <label className="account-field-label">Address</label>
                <input
                  type="text"
                  className="form-control account-input"
                  value={formState.business_street_address}
                  onChange={handleChange("business_street_address")}
                  placeholder="Enter company address..."
                />
              </div>
              <div className="d-flex flex-column flex-md-row flex-wrap account-field-row">
                <div className="account-field">
                  <label className="account-field-label">City</label>
                  <input
                    type="text"
                    className="form-control account-input"
                    value={formState.business_city}
                    onChange={handleChange("business_city")}
                    placeholder="Enter city"
                  />
                </div>
                <div className="account-field">
                  <label className="account-field-label">State</label>
                  <div className="d-flex align-items-stretch account-input-group">
                    <select
                      className="form-control border-0 account-country-flag-select"
                      value={formState.business_country}
                      onChange={handleCountryChange}
                    >
                      {COUNTRY_OPTIONS.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.code}
                        </option>
                      ))}
                    </select>
                    <select
                      className="form-control border-0 account-region-select"
                      value={formState.business_region}
                      onChange={handleChange("business_region", immediateSave)}
                      style={{ color: formState.business_region ? undefined : 'var(--sl-text-placeholder, #717680)' }}
                    >
                      <option value="">State/Province</option>
                      {getRegionsForCountry(formState.business_country).map((region) => (
                        <option key={region.code} value={region.code}>
                          {region.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="account-field">
                  <label className="account-field-label">Postal Code</label>
                  <input
                    type="text"
                    className="form-control account-input"
                    value={formState.business_postal_code}
                    onChange={handlePostalCodeChange}
                    placeholder="Enter postal code"
                    maxLength={formState.business_country === "CA" ? 7 : 10}
                  />
                </div>
              </div>

              <div className="account-section-label">Billing Contact</div>
              <div className="d-flex flex-column flex-md-row account-field-row">
                <div className="account-field">
                  <label className="account-field-label">First Name</label>
                  <input
                    type="text"
                    className="form-control account-input"
                    value={formState.billing_contact_first_name}
                    onChange={handleChange("billing_contact_first_name")}
                    placeholder="Enter first name"
                  />
                </div>
                <div className="account-field">
                  <label className="account-field-label">Last Name</label>
                  <input
                    type="text"
                    className="form-control account-input"
                    value={formState.billing_contact_last_name}
                    onChange={handleChange("billing_contact_last_name")}
                    placeholder="Enter last name"
                  />
                </div>
                <div className="account-field">
                  <label className="account-field-label">Title</label>
                  <input
                    type="text"
                    className="form-control account-input"
                    value={formState.billing_contact_title}
                    onChange={handleChange("billing_contact_title")}
                    placeholder="Enter title"
                  />
                </div>
              </div>
              <div className="d-flex flex-column flex-md-row account-field-row">
                <div className="account-field">
                  <label className="account-field-label">Email Address</label>
                  <input
                    type="email"
                    className="form-control account-input"
                    value={formState.billing_contact_email}
                    onChange={handleChange("billing_contact_email")}
                    placeholder="Add your email..."
                  />
                </div>
                <div className="account-field">
                  <label className="account-field-label">Mobile Number</label>
                  <input
                    type="tel"
                    className="form-control account-input"
                    value={formState.billing_contact_phone}
                    onChange={handlePhoneChange}
                    placeholder="000-000-0000"
                    maxLength={14}
                  />
                </div>
              </div>
            </div>

          </div>
        </div>

        <div className="w-100 account-sidebar-col">
          <div className="account-sidebar-card">
            <div className="account-sidebar-title">
              Why we need this info
            </div>
            <div className="account-sidebar-body">
              <p>This information helps us set up and maintain the services that power your account behind the scenes.</p>
              <p>We use it to:</p>
              <ul className="pl-3 account-list-disc">
                <li>Verify your organization to reduce fraud and prevent account interruptions</li>
                <li>Handle tax reporting and compliance requirements</li>
                <li>Process payouts and payments securely and on time</li>
                <li>Enable messaging and calling via providers like Twilio</li>
              </ul>
              <p>We only ask for what's required, and most of this is a one-time setup. Keeping it accurate ensures everything runs smoothly as your account grows.</p>
              <p className="mb-0">&#x1f512; Your data is encrypted and used only for operational and legal purposes.</p>
            </div>
          </div>
        </div>
      </div>
      </div>
    </SettingsPageWrapper>
  );
};

export default SettingsAccountDetails;
